<script setup lang="ts">
  import { formatDateTime, formatNumber } from '@/utils/strings';
  import { computed, onMounted, onUnmounted, ref } from 'vue';
  import { Line } from 'vue-chartjs';

  import {
    Chart as ChartJS,
    CategoryScale,
    LinearScale,
    PointElement,
    LineElement,
    Title,
    Tooltip,
    Legend,
    type ChartData,
    type ChartOptions,
  } from 'chart.js';

  ChartJS.register(CategoryScale, LinearScale, PointElement, LineElement, Title, Tooltip, Legend);

  const props = defineProps<{
    labels?: string[];
    data?: {
      reach: number[];
      engagement_rate: number[];
    };
  }>();

  const chartRef = ref<any>(null);

  const data = computed<ChartData<'line'>>(() => {
    // Guard against undefined props
    if (!props.data || !props.labels) {
      return {
        labels: [],
        datasets: [],
      };
    }

    return {
      labels: props.labels,
      datasets: [
        {
          label: 'Reach',
          data: props.data.reach,
          tension: 0.3,
          pointStyle: false,
          borderColor: '#3b82f6',
          fill: '#ff0000',
        },
        {
          label: 'Engagement Rate',
          data: props.data.engagement_rate,
          tension: 0.3,
          pointStyle: false,
          borderColor: '#AFD7FF',
          fill: '#dbeafe',
        },
      ],
    };
  });

  const options = computed<ChartOptions<'line'>>(() => ({
    responsive: true,
    maintainAspectRatio: false,
    plugins: {
      tooltip: {
        position: 'nearest',
        displayColors: false,
        intersect: false,
        backgroundColor: '#011627',
        padding: 10,
        cornerRadius: 8,
        yAlign: 'bottom',
        callbacks: {
          title: function (tooltipItems) {
            return 'Date: ' + formatDateTime(tooltipItems[0].label, { format: 'DD MMMM YYYY' });
          },
          label: function (tooltipItem) {
            const datasetLabel = tooltipItem.dataset.label;
            return `${datasetLabel}: ${tooltipItem.formattedValue}`;
          },
        },
      },
      legend: {
        display: false,
      },
    },
    scales: {
      x: {
        grid: {
          display: false,
        },
        ticks: {
          callback: function (val, index) {
            if (!props.labels) return '';
            const totalLabels = props.labels.length;
            const maxLabels = window.innerWidth < 768 ? 6 : 12;
            const step = Math.max(1, Math.floor(totalLabels / maxLabels));
            return index % step === 0 ? formatDateTime(this.getLabelForValue(val as number), { format: 'DD MMM, YY' }) : '';
          },
          maxRotation: 45,
          minRotation: 0,
          font: {
            size: 12,
          },
        },
      },
      y: {
        border: {
          display: false,
        },
        grid: {
          color: '#F7F7F7',
        },
        ticks: {
          font: {
            size: 12,
          },
          callback: val => {
            return formatNumber(String(val));
          },
        },
      },
    },
  }));

  let resizeObserver: ResizeObserver | null = null;

  onMounted(() => {
    // Handle chart resize when container size changes
    const chartContainer = chartRef.value?.$el?.parentElement;
    if (chartContainer) {
      resizeObserver = new ResizeObserver(() => {
        if (chartRef.value?.chart) {
          chartRef.value.chart.resize();
        }
      });
      resizeObserver.observe(chartContainer);
    }
  });

  onUnmounted(() => {
    if (resizeObserver) {
      resizeObserver.disconnect();
    }
  });
</script>

<template>
  <div class="engagement-chart-container flex flex-col gap-24">
    <h2 class="chart-heading">Engagement and Reach Over Time</h2>
    <div class="chart-wrapper">
      <!-- Only render chart when data is available -->
      <Line v-if="props.data && props.labels" ref="chartRef" :data="data" :options="options" />
      <div v-else class="loading-state">Loading chart data...</div>
    </div>
  </div>
</template>

<style scoped lang="scss">
  .loading-state {
    display: flex;
    align-items: center;
    justify-content: center;
    min-height: 300px;
    color: #64748b;
  }

  .engagement-chart-container {
    background-color: white;
    padding: 16px;
    border: 1px solid #e2e8f0;
    border-radius: 0.75rem;
    display: flex;
    flex-direction: column;
    height: 100%;

    .chart-heading {
      font-size: 20px;
      font-weight: 600;
      flex-shrink: 0;

      @media screen and (min-width: 768px) {
        font-size: 24px;
      }
    }
  }

  .chart-wrapper {
    position: relative;
    width: 100%;
    flex: 1;
    min-height: 250px;
    max-height: 500px;

    @media screen and (min-width: 768px) {
      min-height: 300px;
    }

    @media screen and (min-width: 1024px) {
      min-height: 350px;
    }

    canvas {
      width: 100% !important;
      height: 100% !important;
    }
  }
</style>
